import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
	DEFAULT_CONNECTION_DISPLAY_MODE,
	isConnectionDisplayMode,
	type ConnectionDisplayMode,
	type Preferences,
} from '../../../core/ports/preferences-repository';
import { PreferencesService } from '../../../core/services/preferences-service';
import { LocalStoragePreferencesRepository, LOCAL_PREFERENCES_KEY } from '../../../storage/implementations/localstorage-preferences-repository';

const preferencesService = new PreferencesService(new LocalStoragePreferencesRepository());

interface ConnectionDisplayModeContextValue {
	connectionDisplayMode: ConnectionDisplayMode;
	setConnectionDisplayMode(connectionDisplayMode: ConnectionDisplayMode): void;
}

const ConnectionDisplayModeContext = createContext<ConnectionDisplayModeContextValue>({
	connectionDisplayMode: DEFAULT_CONNECTION_DISPLAY_MODE,
	setConnectionDisplayMode: () => undefined,
});

export function ConnectionDisplayModeProvider({ children }: { children: ReactNode }) {
	const [connectionDisplayMode, setConnectionDisplayModeState] = useState<ConnectionDisplayMode>(() => readStoredConnectionDisplayMode());

	useEffect(() => {
		let active = true;

		void preferencesService.getConnectionDisplayMode().then((storedConnectionDisplayMode) => {
			if (active) {
				setConnectionDisplayModeState(storedConnectionDisplayMode);
			}
		});

		return () => {
			active = false;
		};
	}, []);

	const value = useMemo<ConnectionDisplayModeContextValue>(() => ({
		connectionDisplayMode,
		setConnectionDisplayMode(nextConnectionDisplayMode) {
			setConnectionDisplayModeState(nextConnectionDisplayMode);
			void preferencesService.setConnectionDisplayMode(nextConnectionDisplayMode);
		},
	}), [connectionDisplayMode]);

	return <ConnectionDisplayModeContext.Provider value={value}>{children}</ConnectionDisplayModeContext.Provider>;
}

export function useConnectionDisplayMode(): ConnectionDisplayModeContextValue {
	return useContext(ConnectionDisplayModeContext);
}

function readStoredConnectionDisplayMode(): ConnectionDisplayMode {
	if (typeof localStorage === 'undefined') {
		return DEFAULT_CONNECTION_DISPLAY_MODE;
	}

	try {
		const raw = localStorage.getItem(LOCAL_PREFERENCES_KEY);
		if (!raw) {
			return DEFAULT_CONNECTION_DISPLAY_MODE;
		}

		const parsed = JSON.parse(raw) as Partial<Preferences>;
		return isConnectionDisplayMode(parsed.connectionDisplayMode) ? parsed.connectionDisplayMode : DEFAULT_CONNECTION_DISPLAY_MODE;
	} catch {
		return DEFAULT_CONNECTION_DISPLAY_MODE;
	}
}
